import { z } from 'zod';
import { platformJsonValueSchema } from '@crest/platform-contracts/settings';
import { platformImportResolutionCapability, platformImportResolutionProofSchema, } from '@crest/platform-contracts/import-resolution';
import { projectsImportResolutionCapability, projectsImportResolutionSchema, } from './projects-import-resolution.contract.js';
export const projectsProjectStaffImportCapability = 'projects.project-staff-import.v1';
export const projectsProjectStaffImportContractVersion = projectsProjectStaffImportCapability;
export const projectsProjectStaffImportManifestVersion = 'projects-project-staff-source-manifest.v1';
export const projectsProjectStaffImportMappingVersion = 'projects-project-staff-normalized-v1';
export const projectsProjectStaffImportEntityType = 'project-staff';
const timestamp = z.string().datetime({ offset: true });
const sourceId = z.string().trim().min(1).max(500);
const tenantKey = z.string().trim().min(1).max(240);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const uuid = z.string().uuid();
export const projectsProjectStaffImportRoleSchema = z.enum([
    'tech',
    'lead_tech',
    'sales_rep',
]);
export const projectsProjectStaffImportWriterFenceSchema = z.strictObject({
    fenceEpoch: z.string().trim().min(1).max(240),
    sourceWriterState: z.enum(['authoritative', 'frozen']),
    targetWriterState: z.literal('disabled'),
    noDualWriters: z.literal(true),
    sourceThrough: timestamp,
    attestationSha256: sha256,
    attestedAt: timestamp,
}).superRefine((value, context) => {
    if (Date.parse(value.attestedAt) < Date.parse(value.sourceThrough)) {
        context.addIssue({ code: 'custom', path: ['attestedAt'], message: 'Writer attestation cannot predate sourceThrough.' });
    }
});
export const projectsProjectStaffImportSourceManifestSchema = z.strictObject({
    manifestVersion: z.literal(projectsProjectStaffImportManifestVersion),
    sourceSystem: z.literal('crest-crm'),
    sourceRepository: z.string().trim().min(1).max(240),
    sourceSystemRevision: z.string().trim().min(1).max(240),
    sourceKind: z.enum(['synthetic', 'sanitized', 'restricted']),
    sourceCommitSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceTreeSha: z.string().regex(/^[a-f0-9]{40}$/),
    sourceSchemaSha256: sha256,
    captureMode: z.enum(['base', 'final-delta']),
    capturedAt: timestamp,
    writerFence: projectsProjectStaffImportWriterFenceSchema,
    tables: z.array(z.strictObject({
        tableName: z.literal('project_staff'),
        rowCount: z.number().int().nonnegative().safe(),
        sha256,
    })).length(1),
}).superRefine((value, context) => {
    if (Date.parse(value.capturedAt) !== Date.parse(value.writerFence.sourceThrough)) {
        context.addIssue({ code: 'custom', path: ['writerFence', 'sourceThrough'], message: 'Writer watermark must equal capture time.' });
    }
    if (value.captureMode === 'final-delta' && value.writerFence.sourceWriterState !== 'frozen') {
        context.addIssue({ code: 'custom', path: ['writerFence', 'sourceWriterState'], message: 'Final delta requires a frozen source writer.' });
    }
});
/** Exact source material. tenant_id is intentionally represented by the envelope, not this payload. */
export const projectsProjectStaffImportSourceSchema = z.strictObject({
    id: sourceId,
    project_id: sourceId,
    user_id: sourceId,
    role: projectsProjectStaffImportRoleSchema.nullable(),
    assigned_at: timestamp.nullable(),
    created_at: timestamp.nullable(),
    updated_at: timestamp.nullable(),
    created_by: sourceId.nullable(),
});
export const projectsProjectStaffImportRowSchema = z.strictObject({
    sequence: z.number().int().positive().safe(),
    sourceTable: z.literal('project_staff'),
    sourceTenantExternalKey: tenantKey,
    sourceId,
    sourceSha256: sha256,
    sourceUpdatedAt: timestamp.nullable(),
    payload: projectsProjectStaffImportSourceSchema,
}).superRefine((row, context) => {
    if (row.sourceId !== row.payload.id)
        context.addIssue({ code: 'custom', path: ['sourceId'], message: 'sourceId must equal payload.id.' });
    if (row.sourceUpdatedAt !== row.payload.updated_at)
        context.addIssue({ code: 'custom', path: ['sourceUpdatedAt'], message: 'sourceUpdatedAt must equal payload.updated_at exactly.' });
});
export const projectsProjectStaffImportSnapshotPayloadSchema = z.strictObject({
    contractVersion: z.literal(projectsProjectStaffImportCapability),
    mappingVersion: z.literal(projectsProjectStaffImportMappingVersion),
    sourceSystem: z.literal('crest-crm'),
    tenantExternalKey: tenantKey,
    runKind: z.enum(['base', 'final-delta']),
    sourceEpoch: z.string().trim().min(1).max(240),
    parentSourceSha256: sha256.nullable(),
    sourceManifest: projectsProjectStaffImportSourceManifestSchema,
    /** Explicit source user -> tenant_memberships source-key bridge supplied by the extractor. */
    membershipSourceMappings: z.array(z.strictObject({
        sourceTenantExternalKey: tenantKey,
        sourceUserId: sourceId,
        membershipSourceExternalKey: sourceId,
    })).max(100_000).optional(),
    rows: z.array(z.unknown()).max(100_000),
}).superRefine((value, context) => {
    if ((value.runKind === 'base') !== (value.parentSourceSha256 === null))
        context.addIssue({ code: 'custom', path: ['parentSourceSha256'], message: 'Base has no parent; final delta requires an exact parent.' });
    if (value.runKind !== value.sourceManifest.captureMode)
        context.addIssue({ code: 'custom', path: ['sourceManifest', 'captureMode'], message: 'Manifest mode must match run kind.' });
    const count = value.rows.filter((row) => typeof row === 'object' && row !== null && row.sourceTable === 'project_staff').length;
    if (value.sourceManifest.tables[0]?.rowCount !== count || count !== value.rows.length)
        context.addIssue({ code: 'custom', path: ['sourceManifest', 'tables'], message: 'Manifest table count must exactly reconcile to snapshot rows.' });
});
export const projectsProjectStaffImportSnapshotSchema = projectsProjectStaffImportSnapshotPayloadSchema.extend({ sourceManifestSha256: sha256, sourceSha256: sha256 });
export const projectsProjectStaffImportTargetPayloadSchema = z.strictObject({
    projectId: uuid,
    membershipId: uuid,
    responsibility: projectsProjectStaffImportRoleSchema,
    assignedAt: timestamp,
    assignedByMembershipId: uuid,
});
export const projectsProjectStaffImportProjectResolutionSchema = z.strictObject({
    sourceExternalKey: sourceId,
    sourceTenantExternalKey: tenantKey,
    resolution: projectsImportResolutionSchema,
    verified: z.literal(true),
}).superRefine((value, context) => {
    if (value.sourceExternalKey !== value.resolution.sourceId || value.resolution.capability !== projectsImportResolutionCapability || value.resolution.entityType !== 'project')
        context.addIssue({ code: 'custom', path: ['resolution'], message: 'Project resolution must bind the supplied source key.' });
});
export const projectsProjectStaffImportMembershipResolutionSchema = z.strictObject({
    sourceExternalKey: sourceId,
    /** Global legacy users.id key bound to this tenant_memberships source row. */
    sourceUserId: sourceId,
    sourceTenantExternalKey: tenantKey,
    resolution: platformImportResolutionProofSchema,
    verified: z.literal(true),
}).superRefine((value, context) => {
    if (value.sourceExternalKey !== value.resolution.sourceId || value.resolution.capability !== platformImportResolutionCapability || value.resolution.entityType !== 'membership')
        context.addIssue({ code: 'custom', path: ['resolution'], message: 'Membership resolution must bind the supplied source key.' });
    if (value.sourceTenantExternalKey !== value.resolution.sourceTenantExternalKey)
        context.addIssue({ code: 'custom', path: ['sourceTenantExternalKey'], message: 'Membership resolution must be for the supplied source tenant.' });
});
export const projectsProjectStaffImportResolutionSchema = z.union([
    projectsProjectStaffImportProjectResolutionSchema,
    projectsProjectStaffImportMembershipResolutionSchema,
]);
export const projectsProjectStaffImportRejectReasonSchema = z.enum([
    'schema-invalid', 'cross-tenant', 'duplicate-source-identity',
    'source-timestamp-mismatch', 'source-after-capture', 'missing-required-value',
    'unresolved-project', 'unresolved-membership', 'unresolved-assigned-by-membership',
]);
export const projectsProjectStaffImportRejectSchema = z.strictObject({
    sequence: z.number().int().positive().safe(), entityType: z.literal(projectsProjectStaffImportEntityType), sourceId, sourceSha256: sha256,
    reasonCode: projectsProjectStaffImportRejectReasonSchema,
    fieldPaths: z.array(z.string().trim().min(1).max(240)).max(20),
});
export const projectsProjectStaffImportSourceEvidenceSchema = projectsProjectStaffImportSourceSchema;
export const projectsProjectStaffImportIdentityStrategySchema = z.literal('deterministic-v5');
export const projectsProjectStaffImportPlanActionSchema = z.strictObject({
    sequence: z.number().int().positive().safe(), entityType: z.literal(projectsProjectStaffImportEntityType), sourceId, sourceSha256: sha256, sourceUpdatedAt: timestamp,
    targetId: uuid, identityStrategy: projectsProjectStaffImportIdentityStrategySchema,
    payload: projectsProjectStaffImportTargetPayloadSchema,
    sourceEvidence: projectsProjectStaffImportSourceEvidenceSchema,
    projectResolution: projectsProjectStaffImportProjectResolutionSchema,
    membershipResolution: projectsProjectStaffImportMembershipResolutionSchema,
    assignedByMembershipResolution: projectsProjectStaffImportMembershipResolutionSchema,
}).superRefine((action, context) => {
    if (action.projectResolution.sourceExternalKey !== action.sourceEvidence.project_id || action.payload.projectId !== action.projectResolution.resolution.targetId)
        context.addIssue({ code: 'custom', path: ['projectResolution'], message: 'Project resolution and target must bind source evidence.' });
    if (action.membershipResolution.sourceUserId !== action.sourceEvidence.user_id || action.payload.membershipId !== action.membershipResolution.resolution.targetId)
        context.addIssue({ code: 'custom', path: ['membershipResolution'], message: 'Membership resolution and target must bind source evidence.' });
    if (action.assignedByMembershipResolution.sourceUserId !== action.sourceEvidence.created_by || action.payload.assignedByMembershipId !== action.assignedByMembershipResolution.resolution.targetId)
        context.addIssue({ code: 'custom', path: ['assignedByMembershipResolution'], message: 'Assigned-by resolution and target must bind source evidence.' });
});
export const projectsProjectStaffImportPlanSchema = z.strictObject({
    contractVersion: z.literal(projectsProjectStaffImportCapability), manifestVersion: z.literal(projectsProjectStaffImportManifestVersion), mappingVersion: z.literal(projectsProjectStaffImportMappingVersion), sourceSystem: z.literal('crest-crm'), sourceRepository: z.string().trim().min(1).max(240), sourceSystemRevision: z.string().trim().min(1).max(240), sourceKind: z.enum(['synthetic', 'sanitized', 'restricted']), sourceCommitSha: z.string().regex(/^[a-f0-9]{40}$/), sourceTreeSha: z.string().regex(/^[a-f0-9]{40}$/), sourceSchemaSha256: sha256, sourceManifestSha256: sha256, tenantExternalKey: tenantKey, runKind: z.enum(['base', 'final-delta']), sourceEpoch: z.string().trim().min(1).max(240), parentSourceSha256: sha256.nullable(), capturedAt: timestamp, writerFence: projectsProjectStaffImportWriterFenceSchema, sourceSha256: sha256, planSha256: sha256, actions: z.array(projectsProjectStaffImportPlanActionSchema), rejects: z.array(projectsProjectStaffImportRejectSchema), counts: z.strictObject({ sourceRows: z.number().int().nonnegative(), plannedRows: z.number().int().nonnegative(), rejectedRows: z.number().int().nonnegative() }),
}).superRefine((value, context) => { if (value.counts.sourceRows !== value.actions.length + value.rejects.length || value.counts.plannedRows !== value.actions.length || value.counts.rejectedRows !== value.rejects.length)
    context.addIssue({ code: 'custom', path: ['counts'], message: 'Every source row produces exactly one action or reject.' }); });
export const projectsProjectStaffImportExecutionReceiptSchema = z.strictObject({
    sequence: z.number().int().positive().safe(), entityType: z.literal(projectsProjectStaffImportEntityType), sourceId, sourceSha256: sha256, sourceUpdatedAt: timestamp, targetId: uuid, identityStrategy: projectsProjectStaffImportIdentityStrategySchema, outcome: z.enum(['applied', 'skipped', 'verified']), previousTargetSha256: sha256.nullable(), targetSha256: sha256, targetCanonical: projectsProjectStaffImportTargetPayloadSchema, rollbackToken: platformJsonValueSchema,
});
export const projectsProjectStaffImportRollbackReceiptSchema = z.strictObject({ sequence: z.number().int().positive().safe(), outcome: z.enum(['applied', 'already-rolled-back', 'rejected-conflict', 'rejected-state']), detailCode: z.string().trim().min(3).max(120), targetSha256: sha256.nullable() });
//# sourceMappingURL=projects-project-staff-import.contract.js.map